import { createSlice } from '@reduxjs/toolkit';
import type { AIChatMessage } from './types';

// State interface
interface AIChatState {
  messages: AIChatMessage[];
  isLoading: boolean;
  error: boolean;
  topicGuid: string | null;
  topicTitle: string | null;
  dailyRemaining: number | null;
  dailyLimit: number | null;
  resetsAt: string | null;
}

// Initial state - content is i18n key, resolved in UI
const initialState: AIChatState = {
  messages: [],
  isLoading: false,
  error: false,
  topicGuid: null,
  topicTitle: null,
  dailyRemaining: null,
  dailyLimit: null,
  resetsAt: null
};

// AI Chat slice
export const slice = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    // Add user message
    addUserMessage: (state, action) => {
      const userMessage = {
        id: Date.now().toString(),
        content: action.payload,
        sender: 'user' as const,
        timestamp: new Date()
      };
      state.messages.push(userMessage);
    },

    // Load conversation history (replace entirely so empty history can trigger opening message)
    loadConversationHistory: (state, action) => {
      state.messages = action.payload;
    },

    // Set topic context
    setTopicContext: (state, action) => {
      state.topicGuid = action.payload.topicGuid;
      state.topicTitle = action.payload.topicTitle;
    },

    // Start loading
    startLoading: (state) => {
      state.isLoading = true;
      state.error = false;
    },

    // Stop loading
    stopLoading: (state) => {
      state.isLoading = false;
    },

    // Handle error
    hasError: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Clear messages (empty list; UI shows empty state or opening message is fetched by caller)
    clearMessages: (state) => {
      state.messages = [];
    },

    // Clear error
    clearError: (state) => {
      state.error = false;
    },

    // Update daily usage counters (from /AIChat/usage or from SSE done event)
    setUsageInfo: (
      state,
      action: { payload: { remaining: number; dailyLimit: number; resetsAt: string } }
    ) => {
      state.dailyRemaining = action.payload.remaining;
      state.dailyLimit = action.payload.dailyLimit;
      state.resetsAt = action.payload.resetsAt;
    },

    // ── Streaming ───────────────────────────────────────────────────────────

    // Add a placeholder AI message that will be filled in by stream chunks
    startStreamingMessage: (state, action: { payload: string }) => {
      state.messages.push({
        id: action.payload,
        content: '',
        sender: 'ai',
        timestamp: new Date(),
        isStreaming: true
      });
    },

    // Append a text delta to the streaming message
    appendStreamChunk: (state, action: { payload: { id: string; delta: string } }) => {
      const msg = state.messages.find((m) => m.id === action.payload.id);
      if (msg) {
        msg.content += action.payload.delta;
      }
    },

    // Mark the streaming message as complete and attach metadata
    finishStreamingMessage: (
      state,
      action: {
        payload: {
          id: string;
          suggestedFlashcard?: { front: string; back: string };
          tokenCount?: number;
          remaining?: number;
          dailyLimit?: number;
          resetsAt?: string;
        };
      }
    ) => {
      const msg = state.messages.find((m) => m.id === action.payload.id);
      if (msg) {
        msg.isStreaming = false;
        if (action.payload.suggestedFlashcard) {
          msg.suggestedFlashcard = action.payload.suggestedFlashcard;
        }
        if (action.payload.tokenCount != null) {
          msg.tokenCount = action.payload.tokenCount;
        }
      }
      if (action.payload.remaining != null) {
        state.dailyRemaining = action.payload.remaining;
      }
      if (action.payload.dailyLimit != null) {
        state.dailyLimit = action.payload.dailyLimit;
      }
      if (action.payload.resetsAt != null) {
        state.resetsAt = action.payload.resetsAt;
      }
    }
  }
});

export default slice.reducer;
